import { useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

/**
 * Magnetic CTA  -  drifts toward the cursor inside `radius`, then springs
 * back when the cursor leaves. Spring is overdamped  -  no bounce, no pop.
 *
 * Anti-slop: no glow, no gradient border, no scale. The only hover affect
 * is an underline that draws left-to-right with a hand-tuned ease.
 */
export function MagneticLink({
  children,
  href,
  onClick,
  variant = 'primary',
  strength = 0.32,
  radius = 90,
  className = '',
  style,
  trailing,
}) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 22, mass: 1.1 });
  const sy = useSpring(y, { stiffness: 180, damping: 22, mass: 1.1 });

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const dist = Math.hypot(dx, dy);
    if (dist > radius) {
      x.set(0);
      y.set(0);
      return;
    }
    const falloff = 1 - dist / radius;
    x.set(dx * strength * falloff);
    y.set(dy * strength * falloff);
  };
  const handleLeave = () => {
    x.set(0);
    y.set(0);
  };

  const Tag = href && !onClick ? motion.a : motion.button;

  return (
    <Tag
      ref={ref}
      href={href}
      onClick={onClick}
      onPointerMove={handleMove}
      onPointerLeave={handleLeave}
      style={{ x: sx, y: sy, ...style }}
      className={`magnetic magnetic--${variant} ${className}`}
    >
      <span className="magnetic__label">{children}</span>
      {trailing && <span className="magnetic__trailing">{trailing}</span>}
      <span className="magnetic__rule" aria-hidden />
    </Tag>
  );
}
